import React, { useEffect, useState } from "react";
import { Plus, MoonStars } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function Recovery() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sleep_hours: 7.5, sleep_quality: 7, soreness: 4, stress: 4, notes: "" });

  const load = () => api.get("/recovery").then(({ data }) => setLogs(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/recovery", {
        ...form,
        sleep_hours: Number(form.sleep_hours),
      });
      toast.success("Recovery logged.");
      setOpen(false);
      setForm({ sleep_hours: 7.5, sleep_quality: 7, soreness: 4, stress: 4, notes: "" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const chartData = [...logs].reverse().slice(-14).map((l) => ({
    date: new Date(l.date).toLocaleDateString("en", { month: "short", day: "numeric" }),
    score: l.recovery_score,
    sleep: l.sleep_hours,
  }));

  const latest = logs[0];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="recovery-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// REGEN PROTOCOL</div>
          <h1 className="font-heading text-4xl sm:text-5xl">RECOVERY & SLEEP</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="add-recovery-button">
              <Plus size={14} weight="bold" /> LOG RECOVERY
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="recovery-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">DAILY CHECK-IN</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div>
                <label className="label-tiny block mb-2">SLEEP HOURS</label>
                <Input type="number" step="0.5" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="sleep-hours-input" />
              </div>
              {[
                { key: "sleep_quality", label: "SLEEP QUALITY", invert: false },
                { key: "soreness", label: "MUSCLE SORENESS", invert: true },
                { key: "stress", label: "STRESS LEVEL", invert: true },
              ].map((s) => (
                <div key={s.key}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="label-tiny">{s.label}</label>
                    <span className="font-heading text-lg text-brand-accent tabular-nums">{form[s.key]}/10</span>
                  </div>
                  <Slider
                    value={[form[s.key]]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={(v) => setForm({ ...form, [s.key]: v[0] })}
                    data-testid={`${s.key}-slider`}
                  />
                </div>
              ))}
              <div>
                <label className="label-tiny block mb-2">NOTES</label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="recovery-notes-input" />
              </div>
              <button onClick={save} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest" data-testid="save-recovery-button">
                SAVE LOG
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="tactical-card p-6" data-testid="recovery-score-card">
          <div className="label-tiny mb-3">RECOVERY SCORE</div>
          <div className="font-heading text-6xl tabular-nums text-brand-accent neon-yellow">
            {latest?.recovery_score ?? "—"}
          </div>
          <div className="label-tiny mt-2 text-text-secondary">/ 100 (LAST LOG)</div>
        </div>
        <div className="tactical-card p-6" data-testid="sleep-card">
          <div className="flex items-center justify-between mb-3">
            <MoonStars size={20} weight="duotone" className="text-brand-primary" />
            <span className="label-tiny">SLEEP</span>
          </div>
          <div className="font-heading text-5xl tabular-nums">{latest?.sleep_hours ?? "—"}<span className="text-2xl">H</span></div>
          <div className="label-tiny mt-2 text-text-secondary">QUALITY: {latest?.sleep_quality ?? "—"}/10</div>
        </div>
        <div className="tactical-card p-6" data-testid="readiness-card">
          <div className="label-tiny mb-3">READINESS</div>
          <div className="font-heading text-3xl">
            {!latest ? "—" : latest.recovery_score >= 75 ? "GREEN" : latest.recovery_score >= 50 ? "YELLOW" : "RED"}
          </div>
          <div className="text-xs text-text-secondary mt-2">
            {!latest ? "Log a session to see status" : latest.recovery_score >= 75 ? "Full training capacity" : latest.recovery_score >= 50 ? "Moderate intensity advised" : "Active recovery / rest"}
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <div className="tactical-card p-6 mb-8" data-testid="recovery-chart">
          <h3 className="font-heading text-lg mb-4">RECOVERY TREND</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#A1A5B5" fontSize={11} />
              <YAxis stroke="#A1A5B5" fontSize={11} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Line type="monotone" dataKey="score" stroke="#FFEA00" strokeWidth={2} dot={{ r: 4, fill: "#4169E1" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {logs.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="recovery-empty">
          <MoonStars size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">NO RECOVERY DATA</h3>
          <p className="text-sm text-text-secondary">Log your first day to start tracking readiness.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="recovery-logs-list">
          {logs.map((l) => (
            <div key={l.rec_id} className="tactical-card p-5 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div>
                <div className="label-tiny mb-1">DATE</div>
                <div>{new Date(l.date).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="label-tiny mb-1">SCORE</div>
                <div className="font-heading text-lg text-brand-accent tabular-nums">{l.recovery_score}</div>
              </div>
              <div>
                <div className="label-tiny mb-1">SLEEP</div>
                <div className="tabular-nums">{l.sleep_hours}H / {l.sleep_quality}</div>
              </div>
              <div>
                <div className="label-tiny mb-1">SORENESS</div>
                <div className="tabular-nums">{l.soreness}/10</div>
              </div>
              <div>
                <div className="label-tiny mb-1">STRESS</div>
                <div className="tabular-nums">{l.stress}/10</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
